import type { SupplierIndustryKind } from '@tandemone/types'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { getSupplierIndustries } from './api'
import { AvailableCard } from './available-card'
import { SUPPLIER_INDUSTRY_OPTIONS } from './enum-labels'
import { pickerFooterLabel } from './enum-picker'
import { FormSkeleton } from './form-skeleton'
import {
  IndustriesFormProvider,
  NONE_INDUSTRY,
  REAL_INDUSTRY_KINDS
} from './industries-provider'
import { NoneButton } from './none-button'
import { NoneSelectedRow } from './none-selected-row'
import { PrimaryButton } from './primary-button'
import { SelectedCard } from './selected-card'
import { StepFooter } from './step-footer'
import { useIndustriesForm } from './use-industries-form'
import { YearsInput } from './years-input'

export function SupplierIndustriesForm({
  onSuccess,
  submitLabel = 'Save and continue'
}: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const { data } = useQuery({
    queryKey: ['supplier-industries'],
    queryFn: ({ signal }) => getSupplierIndustries(signal)
  })

  if (!data) return <FormSkeleton />
  return (
    <IndustriesFormProvider initial={data.industries} onSuccess={onSuccess}>
      <FormBody submitLabel={submitLabel} />
    </IndustriesFormProvider>
  )
}

function FormBody({ submitLabel }: { submitLabel: string }) {
  const { state, derived, dispatch, isSubmitting, handleSubmit } =
    useIndustriesForm()
  const { noneSelected } = derived
  /**
   * tracks the kind whose card was just opened so its years input gets
   * autofocus on mount. cleared on next interaction so the input isn't
   * re-focused if the parent re-renders.
   */
  const [justAdded, setJustAdded] = useState<SupplierIndustryKind | null>(null)

  return (
    <form onSubmit={handleSubmit} className='flex w-full flex-col gap-4'>
      <section className='rounded-2xl border border-border bg-page p-6'>
        {noneSelected ? (
          <NoneSelectedRow
            option={SUPPLIER_INDUSTRY_OPTIONS[NONE_INDUSTRY]}
            onClear={() => dispatch({ type: 'clearNone' })}
          />
        ) : (
          <>
            <ul className='grid gap-3 sm:grid-cols-2'>
              {REAL_INDUSTRY_KINDS.map(kind => {
                const row = state[kind]
                const option = SUPPLIER_INDUSTRY_OPTIONS[kind]
                if (!row.selected) {
                  return (
                    <AvailableCard
                      key={kind}
                      option={option}
                      onClick={() => {
                        setJustAdded(kind)
                        dispatch({ type: 'add', kind })
                      }}
                    />
                  )
                }
                const yearsMissing = row.yearsInBusiness === 0
                return (
                  <SelectedCard
                    key={kind}
                    option={option}
                    incomplete={yearsMissing}
                    onRemove={() => dispatch({ type: 'remove', kind })}
                  >
                    <YearsInput
                      id={`${kind}-years`}
                      value={row.yearsInBusiness}
                      missing={yearsMissing}
                      autoFocus={justAdded === kind}
                      onChange={years =>
                        dispatch({ type: 'setYears', kind, years })
                      }
                    />
                  </SelectedCard>
                )
              })}
            </ul>
            <NoneButton
              label='None of the above. My business does not serve any of these.'
              onClick={() => dispatch({ type: 'selectNone' })}
            />
          </>
        )}
      </section>

      {state.submitError && (
        <p className='text-sm text-red-600'>{state.submitError}</p>
      )}

      <StepFooter hint={pickerFooterLabel(derived, REAL_INDUSTRY_KINDS.length)}>
        <PrimaryButton
          type='submit'
          disabled={!derived.isValid || isSubmitting}
        >
          {isSubmitting ? 'Saving…' : submitLabel}
        </PrimaryButton>
      </StepFooter>
    </form>
  )
}
